// Documentazione API — sezioni "BookingDocs". JSX estratto verbatim da DeveloperSettings.tsx.
import type { ReactElement } from 'react';
import { getPublicBaseUrl } from '@/lib/api';
import { DocSection, Endpoint, CodeBlock } from './primitives';

export function renderBookingDocSection(section: string): ReactElement | null {
  switch (section) {
      case 'calendar-appointments':
        return (
          <DocSection id="calendar-appointments">
            <h3 className="text-lg font-semibold">Calendar / Appointments</h3>
            <p className="text-sm text-muted-foreground">
              API tenant-scoped per la gestione di appuntamenti, tipologie servizio, slot di disponibilità,
              eccezioni, turni HR, promemoria, preferenze di notifica, automazioni e sync Google Calendar.
              Richiede autenticazione (Bearer token o sessione).
            </p>

            <h4 className="text-sm font-semibold mt-4">Appuntamenti (Secret API v2)</h4>
            <p className="text-sm text-muted-foreground">Base: <code className="bg-muted px-1 rounded">/api/v2/calendar</code> — header <code>X-API-Key: sk_live_…</code>, scope <code>calendar:read</code> / <code>calendar:write</code>.</p>
            <Endpoint method="GET" path="/services" desc="Elenco tipi servizio attivi (durata, colore, skill)" />
            <Endpoint method="GET" path="/consultants?serviceId=UUID" desc="Consulenti pubblici (filtrabili per skill del servizio)" />
            <Endpoint method="GET" path="/availability?date=YYYY-MM-DD&consultantId=UUID&serviceId=UUID" desc="Slot disponibili (turni + eccezioni + appuntamenti + buffer_before/after)" />
            <Endpoint method="GET" path="/forms" desc="Lista form di prenotazione (captcha_secret_key escluso)" />
            <Endpoint method="POST" path="/forms/:slug/submit" desc="Crea prenotazione tramite un form (server-to-server, captcha bypassata)" />
            <Endpoint method="GET" path="/appointments" desc="Elenco appuntamenti (filtri: start, end, status, serviceId, consultantId)" />
            <Endpoint method="GET" path="/appointments/:id" desc="Dettaglio appuntamento" />
            <Endpoint method="POST" path="/appointments" desc="Crea appuntamento (Idempotency-Key supportata)" />
            <Endpoint method="PATCH" path="/appointments/:id" desc="Aggiorna campi (titolo, orari, status, consulente, metadata...)" />
            <Endpoint method="POST" path="/appointments/:id/cancel" desc="Cancella appuntamento (body vuoto)" />
            <Endpoint method="POST" path="/appointments/:id/confirm" desc="Conferma appuntamento (body vuoto)" />
            <Endpoint method="GET" path="/audit/overrides?days=30&limit=100&flag=outside_shift|without_clockin" desc="Audit appuntamenti creati con override eligibilità" />

            <h4 className="text-sm font-semibold mt-4">Tipi di servizio</h4>
            <Endpoint method="GET" path="/types" desc="Tipi appuntamento (durata, colore, skill richieste)" />
            <Endpoint method="POST" path="/types" desc="Crea tipo servizio" />
            <Endpoint method="PUT" path="/types/:id" desc="Aggiorna tipo servizio" />
            <Endpoint method="DELETE" path="/types/:id" desc="Disattiva tipo servizio" />

            <h4 className="text-sm font-semibold mt-4">Eccezioni & Turni</h4>
            <Endpoint method="GET" path="/exceptions" desc="Eccezioni di calendario (chiusure, ferie, festivi)" />
            <Endpoint method="POST" path="/exceptions" desc="Crea eccezione (date, user_id opzionale, is_available)" />
            <Endpoint method="DELETE" path="/exceptions/:id" desc="Rimuove eccezione" />
            <Endpoint method="GET" path="/shifts" desc="Turni HR per consulente (orari di disponibilità settimanali)" />
            <Endpoint method="POST" path="/shifts" desc="Imposta orari di disponibilità" />

            <h4 className="text-sm font-semibold mt-4">Automazioni & Notifiche</h4>
            <Endpoint method="GET" path="/automations" desc="Regole di automazione su eventi (created/updated/cancelled)" />
            <Endpoint method="POST" path="/automations" desc="Crea regola (trigger + azioni email/webhook)" />
            <Endpoint method="GET" path="/notification-preferences" desc="Preferenze notifiche per utente (email, reminder)" />
            <Endpoint method="PUT" path="/notification-preferences" desc="Aggiorna preferenze" />

            <h4 className="text-sm font-semibold mt-4">Google Calendar Sync</h4>
            <Endpoint method="GET" path="/google/status" desc="Stato sincronizzazione Google per utente" />
            <Endpoint method="POST" path="/google/connect" desc="Inizia OAuth flow Google Calendar" />
            <Endpoint method="POST" path="/google/sync/:appointmentId" desc="Forza sync di un appuntamento" />

            <h4 className="text-sm font-semibold mt-4">Stati supportati</h4>
            <p className="text-sm text-muted-foreground">
              <code>scheduled</code>, <code>confirmed</code>, <code>cancelled</code>, <code>completed</code>, <code>no_show</code>.
              Le prenotazioni dal Public Booking API non auto-confermate restano <code>scheduled</code> sul DB ma
              vengono esposte come <code>pending</code> nelle risposte pubbliche.
            </p>

            <CodeBlock title="curl — Servizi">{`curl ${getPublicBaseUrl()}/api/v2/calendar/services \\
  -H "X-API-Key: sk_live_xxx"`}</CodeBlock>

            <CodeBlock title="curl — Consulenti (filtrati per servizio)">{`curl "${getPublicBaseUrl()}/api/v2/calendar/consultants?serviceId=UUID" \\
  -H "X-API-Key: sk_live_xxx"`}</CodeBlock>

            <CodeBlock title="curl — Slot disponibili">{`curl "${getPublicBaseUrl()}/api/v2/calendar/availability?date=2026-04-20&consultantId=UUID&serviceId=UUID" \\
  -H "X-API-Key: sk_live_xxx"`}</CodeBlock>

            <CodeBlock title="curl — Lista form">{`curl ${getPublicBaseUrl()}/api/v2/calendar/forms \\
  -H "X-API-Key: sk_live_xxx"`}</CodeBlock>

            <CodeBlock title="curl — Submit form">{`curl -X POST ${getPublicBaseUrl()}/api/v2/calendar/forms/consulenza-roma/submit \\
  -H "X-API-Key: sk_live_xxx" \\
  -H "Content-Type: application/json" \\
  -d '{
    "serviceId": "UUID",
    "consultantId": "UUID",
    "start": "2026-04-20T10:00:00.000Z",
    "customerName": "Mario Rossi",
    "customerEmail": "mario@example.com",
    "customerPhone": "+39 333 1234567"
  }'`}</CodeBlock>

            <CodeBlock title="curl — Lista appuntamenti">{`curl "${getPublicBaseUrl()}/api/v2/calendar/appointments?start=2026-04-20T00:00:00Z&end=2026-04-21T00:00:00Z&status=confirmed" \\
  -H "X-API-Key: sk_live_xxx"`}</CodeBlock>

            <CodeBlock title="curl — Dettaglio appuntamento">{`curl ${getPublicBaseUrl()}/api/v2/calendar/appointments/$APPOINTMENT_ID \\
  -H "X-API-Key: sk_live_xxx"`}</CodeBlock>

            <CodeBlock title="curl — Crea appuntamento (Idempotency-Key supportata)">{`curl -X POST ${getPublicBaseUrl()}/api/v2/calendar/appointments \\
  -H "X-API-Key: sk_live_xxx" \\
  -H "Idempotency-Key: 8e7b3e6a-1d4b-4d3f-9c8b-2f6e9a0c3a1d" \\
  -H "Content-Type: application/json" \\
  -d '{
    "serviceId": "UUID",
    "consultantId": "UUID consulente",
    "title": "Consulenza Mario Rossi",
    "start": "2026-04-20T10:00:00.000Z",
    "end": "2026-04-20T10:30:00.000Z",
    "status": "confirmed",
    "customerName": "Mario Rossi",
    "customerEmail": "mario@example.com",
    "customerPhone": "+39 333 1234567",
    "metadata": { "source": "api_v2" }
  }'`}</CodeBlock>

            <CodeBlock title="curl — Aggiorna appuntamento (PATCH)">{`curl -X PATCH ${getPublicBaseUrl()}/api/v2/calendar/appointments/$APPOINTMENT_ID \\
  -H "X-API-Key: sk_live_xxx" \\
  -H "Content-Type: application/json" \\
  -d '{
    "start": "2026-04-20T11:00:00.000Z",
    "end": "2026-04-20T11:30:00.000Z",
    "notes": "Spostato su richiesta cliente"
  }'`}</CodeBlock>

            <CodeBlock title="curl — Cancel (body vuoto)">{`curl -X POST ${getPublicBaseUrl()}/api/v2/calendar/appointments/$APPOINTMENT_ID/cancel \\
  -H "X-API-Key: sk_live_xxx"`}</CodeBlock>

            <CodeBlock title="curl — Confirm (body vuoto)">{`curl -X POST ${getPublicBaseUrl()}/api/v2/calendar/appointments/$APPOINTMENT_ID/confirm \\
  -H "X-API-Key: sk_live_xxx"`}</CodeBlock>

            <CodeBlock title="curl — Audit overrides ultimi 30 giorni">{`curl "${getPublicBaseUrl()}/api/v2/calendar/audit/overrides?days=30&limit=100" \\
  -H "X-API-Key: sk_live_xxx"`}</CodeBlock>

            <h4 className="text-sm font-semibold mt-4">Header Idempotency-Key</h4>
            <p className="text-sm text-muted-foreground">
              Il <code>POST /api/v2/calendar/appointments</code> accetta l'header <code>Idempotency-Key</code> (max 255 caratteri,
              richiede una API key autenticata). Una stessa coppia (key + body identico) entro 24h restituisce la stessa risposta
              originale; una richiesta successiva con la stessa key ma body differente restituisce <code>409</code> con
              <code>error_code: "IDEMPOTENCY_MISMATCH"</code>; richieste concorrenti mentre la prima è ancora in elaborazione
              ricevono <code>409</code> con <code>error_code: "IDEMPOTENCY_IN_PROGRESS"</code>. La unicità è per (tenant, api_key, key)
              quindi due integrazioni con API key diverse nello stesso tenant non si influenzano.
            </p>

            <h4 className="text-sm font-semibold mt-4">Codici errore standardizzati</h4>
            <p className="text-sm text-muted-foreground">
              Tutte le risposte di errore del namespace <code>/api/v2/calendar</code> includono un <code>error_code</code>:
              <code>VALIDATION_ERROR</code> (400), <code>NOT_FOUND</code> (404), <code>CONFLICT</code> (409),
              <code>IDEMPOTENCY_MISMATCH</code> (409), <code>IDEMPOTENCY_IN_PROGRESS</code> (409),
              <code>INTERNAL_ERROR</code> (500). Le richieste a <code>/cancel</code> e <code>/confirm</code> richiedono un body vuoto:
              campi extra causano <code>400 VALIDATION_ERROR</code>.
            </p>
          </DocSection>
        );

      case 'public-booking':
        return (
          <DocSection id="public-booking">
            <h3 className="text-lg font-semibold">Public Booking API</h3>
            <p className="text-sm text-muted-foreground">
              API pubbliche (no auth) per i widget di prenotazione esterni. Tutte le richieste sono soggette a rate limit
              di 10 req/min per slug+IP (token endpoints: 10 req/min per token+IP). Honeypot, captcha (hCaptcha o reCAPTCHA)
              e blocco temporaneo IP per abuso sono supportati.
            </p>

            <h4 className="text-sm font-semibold mt-4">Endpoints</h4>
            <p className="text-sm text-muted-foreground">Base: <code className="bg-muted px-1 rounded">/api/calendar/public</code></p>
            <Endpoint method="GET" path="/forms/:slug" desc="Configurazione pubblica del form (branding, campi, deadlines, captcha)" />
            <Endpoint method="GET" path="/forms/:slug/services" desc="Elenco servizi disponibili con skill richieste" />
            <Endpoint method="GET" path="/forms/:slug/consultants?serviceId=UUID" desc="Consulenti disponibili filtrati per skill richieste dal servizio" />
            <Endpoint method="GET" path="/forms/:slug/slots?serviceId=UUID&consultantId=UUID|any&date=YYYY-MM-DD" desc="Slot disponibili nella data richiesta" />
            <Endpoint method="POST" path="/forms/:slug/book" desc="Crea prenotazione (auto-confirm o scheduled in base alla configurazione)" />
            <Endpoint method="GET" path="/appointments/public/:cancelToken" desc="Dettagli appuntamento via token JWT firmato" />
            <Endpoint method="POST" path="/appointments/public/:cancelToken/cancel" desc="Cancellazione (rispetta deadline configurata)" />
            <Endpoint method="POST" path="/appointments/public/:cancelToken/reschedule" desc={`Sposta l'appuntamento a un nuovo orario (rispetta deadline configurata), con body { "start": "ISO8601" }`} />

            <h4 className="text-sm font-semibold mt-4">Body Booking</h4>
            <CodeBlock title="POST /api/calendar/public/forms/:slug/book">{`{
  "serviceId": "UUID del servizio",
  "consultantId": "UUID consulente | 'any'",
  "start": "2026-04-20T10:00:00.000Z",
  "customerName": "Mario Rossi",
  "customerEmail": "mario@example.com",
  "customerPhone": "+39 333 1234567",
  "notes": "Note opzionali",
  "privacyAccepted": true,
  "customFields": { "company": "Acme" },
  "utm": { "utm_source": "google" },
  "captchaToken": "token captcha (se richiesto)",
  "website_url": ""   // honeypot: lasciare vuoto, nome configurabile
}`}</CodeBlock>

            <CodeBlock title="Risposta">{`{
  "success": true,
  "data": {
    "appointmentId": "UUID",
    "cancelToken": "JWT token (TTL configurabile, default 90 giorni)",
    "status": "confirmed | pending"
  }
}`}</CodeBlock>

            <h4 className="text-sm font-semibold mt-4">Esempi curl</h4>
            <CodeBlock title="GET form config">{`curl ${getPublicBaseUrl()}/api/calendar/public/forms/consulenza-roma`}</CodeBlock>
            <CodeBlock title="GET servizi">{`curl ${getPublicBaseUrl()}/api/calendar/public/forms/consulenza-roma/services`}</CodeBlock>
            <CodeBlock title="GET consulenti per servizio">{`curl "${getPublicBaseUrl()}/api/calendar/public/forms/consulenza-roma/consultants?serviceId=UUID"`}</CodeBlock>
            <CodeBlock title="GET slot disponibili">{`curl "${getPublicBaseUrl()}/api/calendar/public/forms/consulenza-roma/slots?serviceId=UUID&consultantId=UUID&date=2026-04-20"`}</CodeBlock>
            <CodeBlock title="POST prenotazione">{`curl -X POST ${getPublicBaseUrl()}/api/calendar/public/forms/consulenza-roma/book \\
  -H "Content-Type: application/json" \\
  -d '{
    "serviceId": "UUID",
    "consultantId": "UUID|any",
    "start": "2026-04-20T10:00:00.000Z",
    "customerName": "Mario Rossi",
    "customerEmail": "mario@example.com",
    "customerPhone": "+39 333 1234567",
    "privacyAccepted": true,
    "captchaToken": "...",
    "website_url": ""
  }'`}</CodeBlock>
            <CodeBlock title="GET dettagli appuntamento via token">{`curl ${getPublicBaseUrl()}/api/calendar/public/appointments/public/$TOKEN`}</CodeBlock>
            <CodeBlock title="POST cancel">{`curl -X POST ${getPublicBaseUrl()}/api/calendar/public/appointments/public/$TOKEN/cancel`}</CodeBlock>
            <CodeBlock title="POST reschedule">{`curl -X POST ${getPublicBaseUrl()}/api/calendar/public/appointments/public/$TOKEN/reschedule \\
  -H "Content-Type: application/json" \\
  -d '{ "start": "2026-04-21T10:00:00.000Z" }'`}</CodeBlock>

            <h4 className="text-sm font-semibold mt-4">MCP Tool — create_appointment_with_customer (atomico)</h4>
            <p className="text-sm text-muted-foreground">
              Tool MCP <strong>atomico e transazionale</strong> che assorbe il vecchio <code>submit_appointment_request_form</code>
              e il pattern manuale "cerca cliente → crea cliente → crea appuntamento". Esegue find-or-create del cliente
              (per email/telefono), advisory lock sullo slot, validazione, inserimento appuntamento e automazioni in
              un'unica transazione PostgreSQL. Se un passo fallisce rollback completo: nessun cliente fantasma, nessun appuntamento orfano.
              Valido per chatbot web, voicebot inbound e assistenti AI interni — è lo stesso server MCP.
            </p>

            <h4 className="text-sm font-semibold mt-2">Decision tree</h4>
            <ul className="list-disc pl-5 text-sm text-muted-foreground space-y-1">
              <li>Prenotazione su form pubblico (chatbot widget / link) → passa <code>form_slug</code>, applica branding/whitelist/auto_confirm del form.</li>
              <li>Voicebot inbound o assistente AI che prenota senza form → ometti <code>form_slug</code>, passa <code>source</code> appropriato.</li>
              <li>Operatore autenticato che crea un appuntamento per un cliente già esistente con ID noto → continua a usare <code>create_appointment</code> (assegnazione esplicita, nessun find-or-create).</li>
              <li>Cerca / crea cliente isolato (senza appuntamento) → usa <code>crm_search</code> / creazione dedicata.</li>
            </ul>

            <CodeBlock title="Argomenti (source = chatbot_web con form_slug)">{`{
  "form_slug": "consulenza-roma",
  "service_id": "UUID servizio",
  "consultant_id": "UUID | 'any'",
  "start_time": "2026-04-20T10:00:00.000Z",
  "customer": {
    "name": "Mario Rossi",
    "email": "mario@example.com",
    "phone": "+39 333 1234567",
    "privacy_accepted": true,
    "gdpr_marketing_consent": false
  },
  "source": "chatbot_web",
  "notes": "Preferisce orario mattina"
}`}</CodeBlock>
            <CodeBlock title="Argomenti (source = voicebot_inbound senza form)">{`{
  "service_id": "UUID servizio",
  "consultant_id": "UUID consulente",
  "start_time": "2026-04-20T10:00:00.000Z",
  "customer": {
    "first_name": "Mario",
    "last_name": "Rossi",
    "phone": "+39 333 1234567",
    "privacy_accepted": true
  },
  "source": "voicebot_inbound"
}`}</CodeBlock>
            <CodeBlock title="Argomenti (source = ai_assistant, cliente esistente per email)">{`{
  "service_id": "UUID servizio",
  "start_time": "2026-04-20T10:00:00.000Z",
  "customer": { "email": "mario@example.com" },
  "source": "ai_assistant"
}`}</CodeBlock>
            <CodeBlock title="Risposta">{`{
  "success": true,
  "data": {
    "appointment_id": "UUID",
    "customer_id": "UUID",
    "customer_created": true,
    "consultant_id": "UUID | null",
    "status": "confirmed | pending",
    "cancel_token": "JWT (solo con form_slug)",
    "cancel_link": "https://.../booking/<token> (solo con form_slug)",
    "form_slug": "consulenza-roma",
    "message": "Appuntamento confermato. ID ..."
  }
}`}</CodeBlock>
            <p className="text-sm text-muted-foreground mt-2">
              Valori ammessi per <code>source</code>: <code>chatbot_web</code>, <code>voicebot_inbound</code>, <code>ai_assistant</code>, <code>operator</code>.
              Almeno uno tra <code>customer.email</code> e <code>customer.phone</code> è obbligatorio (chiave di find-or-create).
            </p>

            <h4 className="text-sm font-semibold mt-4">Note di sicurezza</h4>
            <ul className="list-disc pl-5 text-sm text-muted-foreground space-y-1">
              <li>Honeypot configurabile (default <code>website_url</code>): se valorizzato, la richiesta è scartata.</li>
              <li>Captcha: hCaptcha o reCAPTCHA — secret key è server-side, mai esposta al widget.</li>
              <li>Token cancel/reschedule: JWT firmato + hash SHA-256 salvato in DB (lookup costante).</li>
              <li>Anti-abuso: 30 errori 4xx in 10 minuti dallo stesso IP+slug → blocco temporaneo.</li>
            </ul>
          </DocSection>
        );

      case 'admin-booking-forms':
        return (
          <DocSection id="admin-booking-forms">
            <h3 className="text-lg font-semibold">Booking Forms — Admin CRUD</h3>
            <p className="text-sm text-muted-foreground">
              Gestione dei form di prenotazione pubblici. Richiede autenticazione e ruolo admin/manager.
              Ogni form ha uno <code>slug</code> globale unico ed è scopato al tenant del chiamante.
            </p>

            <h4 className="text-sm font-semibold mt-4">Endpoints</h4>
            <p className="text-sm text-muted-foreground">Base: <code className="bg-muted px-1 rounded">/api/v2/calendar/forms</code> (Secret API key, scope <code>calendar:read</code>/<code>calendar:write</code>)</p>
            <Endpoint method="GET" path="/" desc="Elenco form del tenant" />
            <Endpoint method="GET" path="/:id" desc="Dettaglio + servizi e consulenti collegati" />
            <Endpoint method="POST" path="/" desc="Crea nuovo form (richiede slug e name)" />
            <Endpoint method="PUT" path="/:id" desc="Aggiorna form (anche service_ids/consultant_ids)" />
            <Endpoint method="DELETE" path="/:id" desc="Elimina form (cascade su tabelle whitelist)" />

            <h4 className="text-sm font-semibold mt-4">Esempio body</h4>
            <CodeBlock title='POST /api/calendar/forms'>{`{
  "slug": "consulenza-roma",
  "name": "Consulenza Roma",
  "description": "Prenota una consulenza nella sede di Roma",
  "widget_style": "calcom",
  "branding": {
    "primary_color": "#6366f1",
    "logo_url": "https://example.com/logo.png"
  },
  "consultant_mode": "whitelist",
  "consultant_ids": ["UUID-1", "UUID-2"],
  "allow_consultant_choice": true,
  "allow_any_consultant": true,
  "service_mode": "whitelist",
  "service_ids": ["UUID-service-1"],
  "auto_confirm": true,
  "require_customer_phone": true,
  "send_customer_email": true,
  "send_consultant_email": true,
  "attach_ics": true,
  "honeypot_field_name": "website_url",
  "captcha_provider": "hcaptcha",
  "captcha_site_key": "...",
  "captcha_secret_key": "...",
  "cancel_deadline_hours": 24,
  "reschedule_deadline_hours": 24,
  "privacy_text": "Accetto il trattamento dati...",
  "privacy_url": "https://example.com/privacy"
}`}</CodeBlock>

            <h4 className="text-sm font-semibold mt-4">Esempi curl</h4>
            <CodeBlock title="GET lista form">{`curl ${getPublicBaseUrl()}/api/calendar/forms \\
  -H "Authorization: Bearer $ADMIN_TOKEN"`}</CodeBlock>
            <CodeBlock title="POST crea form">{`curl -X POST ${getPublicBaseUrl()}/api/calendar/forms \\
  -H "Authorization: Bearer $ADMIN_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{ "slug": "consulenza-roma", "name": "Consulenza Roma", "auto_confirm": true }'`}</CodeBlock>
            <CodeBlock title="PUT aggiorna form">{`curl -X PUT ${getPublicBaseUrl()}/api/calendar/forms/$FORM_ID \\
  -H "Authorization: Bearer $ADMIN_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{ "name": "Nuovo nome", "is_active": false }'`}</CodeBlock>
            <CodeBlock title="DELETE form">{`curl -X DELETE ${getPublicBaseUrl()}/api/calendar/forms/$FORM_ID \\
  -H "Authorization: Bearer $ADMIN_TOKEN"`}</CodeBlock>

            <h4 className="text-sm font-semibold mt-4">Privacy & sicurezza</h4>
            <ul className="list-disc pl-5 text-sm text-muted-foreground space-y-1">
              <li>Tutti gli endpoint <code>/api/calendar/forms*</code> richiedono ruolo <strong>admin</strong> (router-level guard).</li>
              <li>Il campo <code>captcha_secret_key</code> non viene mai restituito dalle API: nelle response compare solo <code>captcha_secret_configured: boolean</code>.</li>
              <li>TTL dei token cancel/reschedule configurabile via env <code>APPOINTMENT_CANCEL_TOKEN_TTL_DAYS</code> (default 90 giorni).</li>
              <li>Rate limit: 10 richieste/minuto per <code>slug+IP</code> sui form pubblici, 10/min per <code>token+IP</code> sugli endpoint cancel/reschedule.</li>
            </ul>

            <h4 className="text-sm font-semibold mt-4">Filtraggio consulenti per skill</h4>
            <p className="text-sm text-muted-foreground">
              Quando un servizio (<code>service_type</code>) è collegato a uno o più <code>pos_products</code> con skill
              richieste, il sistema filtra automaticamente i consulenti pubblici mostrando solo chi possiede
              <em> tutte </em> le skill (tabella <code>hr_user_skills</code>).
            </p>
          </DocSection>
        );

    default:
      return null;
  }
}
